import React, { useState } from 'react';
import { Monitor, Smartphone, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Toggle } from './ui/toggle';

// Sample data for preview
const samplePerson = {
  first_name: 'John',
  last_name: 'Smith',
  full_name: 'John Smith',
  email: 'john.smith@example.com'
};

// Replace tokens with sample data
const replaceTokens = (html) => {
  return html
    .replace(/\{\{first_name\}\}/g, samplePerson.first_name)
    .replace(/\{\{last_name\}\}/g, samplePerson.last_name)
    .replace(/\{\{full_name\}\}/g, samplePerson.full_name)
    .replace(/\{\{email\}\}/g, samplePerson.email);
};

// Convert editor HTML to email-safe HTML with inline styles
const convertToEmailHtml = (html) => {
  // Add inline styles for email compatibility
  let emailHtml = html
    // Headers
    .replace(/<h1>/g, '<h1 style="font-size: 28px; font-weight: bold; margin: 0 0 16px 0; color: #1a1a1a;">')
    .replace(/<h2>/g, '<h2 style="font-size: 22px; font-weight: bold; margin: 0 0 12px 0; color: #1a1a1a;">')
    .replace(/<h3>/g, '<h3 style="font-size: 18px; font-weight: bold; margin: 0 0 10px 0; color: #1a1a1a;">')
    // Paragraphs
    .replace(/<p>/g, '<p style="margin: 0 0 16px 0; line-height: 1.6; color: #333;">')
    // Links
    .replace(/<a /g, '<a style="color: #2C9DE1; text-decoration: underline;" ')
    // Lists
    .replace(/<ul>/g, '<ul style="margin: 0 0 16px 0; padding-left: 24px;">')
    .replace(/<ol>/g, '<ol style="margin: 0 0 16px 0; padding-left: 24px;">')
    .replace(/<li>/g, '<li style="margin-bottom: 8px;">')
    // Blockquote
    .replace(/<blockquote>/g, '<blockquote style="border-left: 3px solid #2C9DE1; padding-left: 16px; margin: 16px 0; color: #666; font-style: italic;">')
    // Horizontal rule
    .replace(/<hr>/g, '<hr style="border: none; border-top: 2px solid #e5e7eb; margin: 24px 0;">');

  return emailHtml;
};

const EmailPreview = ({ 
  open, 
  onClose, 
  subject, 
  htmlBody,
  senderEmail = 'chris@empmedic.com'
}) => {
  const [viewMode, setViewMode] = useState('desktop');

  const processedBody = replaceTokens(convertToEmailHtml(htmlBody || ''));
  const processedSubject = replaceTokens(subject || '');

  // Marketing emails are self-contained templates - no extra header/footer needed
  const fullEmailHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="font-family: Arial, Helvetica, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 0; background-color: #ffffff;">
      ${processedBody}
    </body>
    </html>
  `;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${viewMode === 'desktop' ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] p-0 transition-all duration-300`}>
        <DialogHeader className="p-4 border-b bg-slate-50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Email Preview
            </DialogTitle>
            <div className="flex items-center gap-2">
              <div className="flex bg-slate-200 rounded-md p-1">
                <Toggle
                  size="sm"
                  pressed={viewMode === 'desktop'}
                  onPressedChange={() => setViewMode('desktop')}
                  className="data-[state=on]:bg-white"
                >
                  <Monitor className="h-4 w-4" />
                </Toggle>
                <Toggle
                  size="sm"
                  pressed={viewMode === 'mobile'}
                  onPressedChange={() => setViewMode('mobile')}
                  className="data-[state=on]:bg-white"
                >
                  <Smartphone className="h-4 w-4" />
                </Toggle>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Email client header simulation */}
        <div className="border-b bg-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-slate-500 w-16">From:</span>
            <span className="text-slate-900">EMP Medic &lt;{senderEmail}&gt;</span>
          </div>
          <div className="flex items-center gap-2 text-sm mt-1">
            <span className="font-medium text-slate-500 w-16">To:</span>
            <span className="text-slate-900">{samplePerson.full_name} &lt;{samplePerson.email}&gt;</span>
          </div>
          <div className="flex items-center gap-2 text-sm mt-1">
            <span className="font-medium text-slate-500 w-16">Subject:</span>
            <span className="text-slate-900 font-medium">{processedSubject || '(No subject)'}</span>
          </div>
        </div>

        {/* Email content */}
        <div 
          className={`overflow-y-auto bg-slate-200 p-4 ${viewMode === 'mobile' ? 'flex justify-center' : ''}`}
          style={{ maxHeight: 'calc(90vh - 200px)' }}
        >
          <div 
            className={`bg-white shadow-lg ${viewMode === 'mobile' ? 'w-[375px]' : 'w-full'}`}
            style={{ 
              borderRadius: viewMode === 'mobile' ? '24px' : '4px',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'mobile' && (
              <div className="bg-slate-900 h-6 flex items-center justify-center">
                <div className="w-16 h-1 bg-slate-700 rounded-full"></div>
              </div>
            )}
            <iframe
              srcDoc={fullEmailHtml}
              title="Email Preview"
              className="w-full border-0"
              style={{ 
                height: viewMode === 'mobile' ? '500px' : '400px',
                display: 'block'
              }}
            />
            {viewMode === 'mobile' && (
              <div className="bg-slate-900 h-8 flex items-center justify-center">
                <div className="w-24 h-1 bg-slate-700 rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* Sample data notice */}
        <div className="p-3 bg-blue-50 border-t text-center text-xs text-blue-600 rounded-b-lg">
          Preview shows sample data: {samplePerson.first_name} {samplePerson.last_name} ({samplePerson.email})
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreview;
